'use client';

import { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import Image from 'next/image';

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isBoldDropdownOpen, setIsBoldDropdownOpen] = useState(false);
  const [isMobileBoldOpen, setIsMobileBoldOpen] = useState(false);
  const pathname = usePathname();
  const boldDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (boldDropdownRef.current && !boldDropdownRef.current.contains(e.target as Node)) {
        setIsBoldDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const navLinks = [
    { id: 'nav_partnerships', label: 'Partnerships', href: '/partnerships-impact' },
    { id: 'nav_contact', label: 'Contact', href: '/contact' },
  ];

  const isActive = (href: string) => pathname === href;
  const isBoldActive =
    pathname === '/bold-through-heart' ||
    pathname === '/bold-through-heart/food-for-thought' ||
    pathname === '/bold-through-heart/opportunity-platform';

  return (
    <>
      {/* Top Banner */}
      <div className="fixed top-0 left-0 w-full z-50 bg-foreground text-background py-3">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <a
            href="https://stan.store/maryambellakbira"
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm font-medium hover:underline inline-flex items-center gap-2"
          >
            FREE Tools & Resources for YOU ❤️
          </a>
        </div>
      </div>

      {/* Main Navigation */}
      <nav
        className={`fixed left-0 w-full z-40 transition-all duration-500 ${
          isScrolled
            ? 'top-0 backdrop-blur-md border-b border-border py-4 shadow-sm'
            : 'top-12 backdrop-blur-sm py-6 border-b border-border/40'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
          {/* Logo */}
          <Link
            href="/homepage"
            className="transition-opacity hover:opacity-70"
          >
            <Image
              src="/assets/images/Black_White_Minimalist_Initials_Monogram_Jewelry_Logo_2_-1770604637172.png"
              alt="Maryam Bellakbira Logo"
              width={70}
              height={70}
              className="object-contain"
              priority
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {/* Home */}
            <Link
              href="/homepage"
              className={`px-6 py-2 rounded-full text-micro tracking-widest transition-all duration-300 ${
                isActive('/homepage')
                  ? 'bg-foreground text-background shadow-sm' :'text-foreground/70 hover:text-foreground hover:bg-foreground/5 nav-link-premium'
              }`}
            >
              Home
            </Link>

            {/* Education & Impact */}
            <Link
              href="/education-impact"
              className={`px-6 py-2 rounded-full text-micro tracking-widest transition-all duration-300 ${
                isActive('/education-impact')
                  ? 'bg-foreground text-background shadow-sm' :'text-foreground/70 hover:text-foreground hover:bg-foreground/5 nav-link-premium'
              }`}
            >
              Education & Impact
            </Link>

            {/* Miss Heart of New York */}
            <Link
              href="/bold-through-heart"
              className={`px-6 py-2 rounded-full text-micro tracking-widest transition-all duration-300 ${
                isBoldActive
                  ? 'bg-foreground text-background shadow-sm'
                  : 'text-foreground/70 hover:text-foreground hover:bg-foreground/5 nav-link-premium'
              }`}
            >
              Miss Heart of New York
            </Link>

            {/* Food for Thought Foundation */}
            <Link
              href="/bold-through-heart/food-for-thought"
              className={`px-6 py-2 rounded-full text-micro tracking-widest transition-all duration-300 ${
                isActive('/bold-through-heart/food-for-thought')
                  ? 'bg-foreground text-background shadow-sm' :'text-foreground/70 hover:text-foreground hover:bg-foreground/5 nav-link-premium'
              }`}
            >
              Food for Thought Foundation
            </Link>

            {/* Remaining links */}
            {navLinks.map((link) => (
              <Link
                key={link.id}
                href={link.href}
                className={`px-6 py-2 rounded-full text-micro tracking-widest transition-all duration-300 ${
                  isActive(link.href)
                    ? 'bg-foreground text-background shadow-sm'
                    : 'text-foreground/70 hover:text-foreground hover:bg-foreground/5 nav-link-premium'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-3 bg-foreground/5 backdrop-blur-md rounded-full border border-border"
          >
            <Icon
              name={isMobileMenuOpen ? 'XMarkIcon' : 'Bars3Icon'}
              size={20}
              className="text-foreground"
            />
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden absolute top-full left-0 w-full bg-white border-b border-border shadow-lg">
            <div className="flex flex-col p-6 gap-2">
              {/* Home */}
              <Link
                href="/homepage"
                onClick={() => setIsMobileMenuOpen(false)}
                className={`px-6 py-3 rounded-lg text-sm font-medium transition-colors ${
                  isActive('/homepage')
                    ? 'bg-foreground text-background' :'text-foreground hover:bg-muted'
                }`}
              >
                Home
              </Link>

              {/* Education & Impact mobile */}
              <Link
                href="/education-impact"
                onClick={() => setIsMobileMenuOpen(false)}
                className={`px-6 py-3 rounded-lg text-sm font-medium transition-colors ${
                  isActive('/education-impact')
                    ? 'bg-foreground text-background' :'text-foreground hover:bg-muted'
                }`}
              >
                Education & Impact
              </Link>

              {/* Lead with H.E.A.R.T. mobile with sub-items */}
              <div>
                <Link
                  href="/bold-through-heart"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`block px-6 py-3 rounded-lg text-sm font-medium transition-colors ${
                    isBoldActive
                      ? 'bg-foreground text-background'
                      : 'text-foreground hover:bg-muted'
                  }`}
                >
                  Miss Heart of New York
                </Link>
              </div>

              {/* Food for Thought Foundation mobile */}
              <Link
                href="/bold-through-heart/food-for-thought"
                onClick={() => setIsMobileMenuOpen(false)}
                className={`block px-6 py-3 rounded-lg text-sm font-medium transition-colors ${
                  isActive('/bold-through-heart/food-for-thought')
                    ? 'bg-foreground text-background' :'text-foreground hover:bg-muted'
                }`}
              >
                Food for Thought Foundation
              </Link>

              {/* Remaining links */}
              {navLinks.map((link) => (
                <Link
                  key={link.id}
                  href={link.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`px-6 py-3 rounded-lg text-sm font-medium transition-colors ${
                    isActive(link.href)
                      ? 'bg-foreground text-background'
                      : 'text-foreground hover:bg-muted'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        )}
      </nav>
    </>
  );
}